// File: ModelingContestsUI.jsx
import { useState, useEffect } from "react";
import Select from "react-select";
import { FilterIcon } from "./CustomSVG";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/Instance";
import { toast } from "sonner";
import ImageSelectionModal from "./ImageSelectionModal";
import PhotoCapturePromptModal from "./PhotoCapturePromptModal";
import Image1 from "../assets/contest images/image1.jpg";
import Image2 from "../assets/contest images/image2.jpg";
import Image3 from "../assets/contest images/image3.jpg";

function CountdownTimer({ targetTime, label = "Starts In", expiredText = "Complete" }) {
  const [status, setStatus] = useState("counting");
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(targetTime));

  function getTimeLeft(time) {
    const diff = new Date(time) - new Date();
    return diff;
  }

  function formatTime(ms) {
    const totalSeconds = Math.floor(Math.abs(ms) / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days > 0 ? `${days}d ` : ""}${String(hours).padStart(
      2,
      "0"
    )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = getTimeLeft(targetTime);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setStatus("expired");
      } else {
        setStatus("counting");
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetTime]);

  if (status === "expired") {
    return <span>{expiredText}</span>;
  }

  return (
    <span>
      {label}: {formatTime(timeLeft)}
    </span>
  );
}

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const filterExpiredContests = (contestsList = []) => {
  const now = Date.now();

  return contestsList.filter((contest) => {
    const rawEndTime = contest?.end_time || contest?.endTime || contest?.endDate;

    if (!rawEndTime) {
      return true;
    }

    const endTime = new Date(rawEndTime).getTime();

    if (Number.isNaN(endTime)) {
      return true;
    }

    const elapsedMs = now - endTime;

    if (elapsedMs <= 0) {
      return true;
    }

    return elapsedMs <= TWENTY_FOUR_HOURS_MS;
  });
};

export default function ModelingContestsUI({ role }) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedParts, setSelectedParts] = useState([]);
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedContestId, setSelectedContestId] = useState(null);
  const [selectedContestName, setSelectedContestName] = useState("");
  const [selectedContestCategory, setSelectedContestCategory] = useState("");
  const [selectedContestDetails, setSelectedContestDetails] = useState(null);
  const [selectedContestResults, setSelectedContestResults] = useState(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isVotingOnly, setIsVotingOnly] = useState(false);
  const [showImageSelection, setShowImageSelection] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [userImages, setUserImages] = useState([]);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const { profile } = useAuth();
  const userRole = profile?.role || "";

  // Top 3 winners
  const topThree = [
    {
      contest: "Beach Body",
      username: "@alice_w",
      votes: 42,
      position: "🥇 1st",
      image: Image1,
    },
    {
      contest: "Perfect Pose",
      username: "@bob_k",
      votes: 35,
      position: "🥈 2nd",
      image: Image2,
    },
    {
      contest: "Best Chest",
      username: "@charlie_z",
      votes: 20,
      position: "🥉 3rd",
      image: Image3,
    },
  ];

  // All contributors full list
  const allContributors = [
    { place: 1, screenName: "sdf", votes: 657 },
    { place: 2, screenName: "hf", votes: 456 },
    { place: 3, screenName: "er", votes: 334 },
    { place: 4, screenName: "uil", votes: 232 },
    { place: 5, screenName: "we", votes: 213 },
    { place: 6, screenName: "dfg", votes: 199 },
    { place: 7, screenName: "ret", votes: 189 },
  ];

  const [allContests, setAllContests] = useState([]);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setJoinedContestIds] = useState([]);
  const [, setIsMobile] = useState(false);
  const [showAllResults] = useState(false);

  const bodyPartOptions = [
    { value: "Teaser", label: "Teaser" },
    { value: "Full Body", label: "Full Body" },
    { value: "Chest & Stomach", label: "Chest & Stomach" },
    { value: "Butt", label: "Butt" },
    { value: "P/V", label: "P/V" },
    { value: "Legs", label: "Legs" },
    { value: "Feet", label: "Feet" },
  ];

  const handleEnterClick = async (contest) => {
    setIsVotingOnly(false);
    setSelectedContestDetails(contest);

    // Users/Judges should confirm before heading to voting (they don't join as participants)
    if (userRole === "user" || userRole === "Judge") {
      setSelectedContestId(contest.id);
      setSelectedContestName(contest.title);
      setSelectedContestCategory(contest.category);
      setIsVotingOnly(true);
      setShowConfirm(true);
      return;
    }

    // For contributors, prep state for join flow
    setSelectedContestId(contest.id);
    setSelectedContestName(contest.title);
    setSelectedContestCategory(contest.category);
    
    if (userRole === "contributor" || userRole === "Content Creator") {
      try {
        const token = profile?.accessToken;
        if (!token) {
          toast.error("❌ Please login to join contests");
          navigate("/login");
          return;
        }

        // Fetch user's images
        const response = await fetch(`${API_BASE}accounts/body-part-images/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch images');
        }

        const images = await response.json();
        
        // Filter images by contest category
        const categoryImages = images.filter(img => 
          img.body_part.toLowerCase() === contest.category.toLowerCase()
        );

        if (categoryImages.length === 0) {
          // No images in this category - show photo capture prompt
          setShowPhotoCapture(true);
        } else if (categoryImages.length === 1) {
          // Exactly one image - use it and show confirmation
          setUserImages(categoryImages);
          setSelectedImageId(categoryImages[0].id);
          setShowConfirm(true);
        } else {
          // Multiple images - show selection modal
          setUserImages(categoryImages);
          setShowImageSelection(true);
        }
      } catch (error) {
        console.error("Error checking images:", error);
        toast.error("❌ Failed to check your images. Please try again.");
      }
    }
  };

  const handleImageSelection = (imageId) => {
    setSelectedImageId(imageId);
    setShowImageSelection(false);
    setShowConfirm(true);
  };

  const confirmEntry = async () => {
    if (isVotingOnly) {
      const contestIdForVote = selectedContestId;

      try {
        setIsJoining(true);
        const token = profile?.accessToken;

        if (!token) {
          toast.error("❌ Please login to enter contests");
          navigate("/login");
          return;
        }

        const response = await fetch(
          `${API_BASE}accounts/contests/${contestIdForVote}/join/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          toast.error(`❌ ${errorData.detail || errorData.error || "Failed to enter contest"}`);
          return;
        }

        const data = await response.json();
        setJoinedContestIds((prev) =>
          prev.includes(contestIdForVote) ? prev : [...prev, contestIdForVote]
        );

        if (data.reentered) {
          toast.success("✅ Re-entered contest without a second payment.");
        } else if (!data.already_joined) {
          toast.success("✅ Entry confirmed.");
        }

        if (data.waiting_room) {
          navigate(`/waiting-room/${contestIdForVote}`);
        } else {
          navigate(`/photo-voting/${contestIdForVote}`);
        }
      } catch (error) {
        console.error("Error entering contest:", error);
        toast.error("❌ An error occurred while entering the contest");
      } finally {
        setShowConfirm(false);
        setIsJoining(false);
        setIsVotingOnly(false);
        setSelectedContestId(null);
        setSelectedContestName("");
        setSelectedContestCategory("");
        setSelectedContestDetails(null);
        setUserImages([]);
        setSelectedImageId(null);
      }
      return;
    }

    if (!selectedContestId) return;
    
    // Use the image from state
    const imageToUse = selectedImageId;
    
    try {
      setIsJoining(true);
      
      const token = profile?.accessToken;
      if (!token) {
        toast.error("❌ Please login to join contests");
        navigate("/login");
        return;
      }
      
      // Prepare request body - include image_id if available
      const requestOptions = {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      // Only include body if we have an image ID
      if (imageToUse) {
        requestOptions.body = JSON.stringify({ body_part_image_id: imageToUse });
      }
      
      const response = await fetch(
        `${API_BASE}accounts/contests/${selectedContestId}/join/`,
        requestOptions
      );

      if (response.ok) {
        const data = await response.json();
        const contestIdForNavigation = selectedContestId;
        
        // Add to joined contests
        setJoinedContestIds(prev => [...prev, selectedContestId]);
        
        // Check if already joined or newly joined
        if (data.already_joined) {
          toast.info(`Already joined "${selectedContestName}".`);
        } else {
          toast.success(`✅ Successfully joined "${selectedContestName}"!`);
        }
        
        // Reset joining state so subsequent actions are not stuck in "Joining..."
        setIsJoining(false);
        
        // Close modal first
        setShowConfirm(false);
        setShowImageSelection(false);
        
        // Reset states
        setUserImages([]);
        setSelectedImageId(null);
        setSelectedContestCategory("");
        setSelectedContestDetails(null);
        setSelectedContestId(null);
        setSelectedContestName("");
        
        // Navigate to voting page or waiting room based on contest start time
        const isUserRole = userRole === "user" || userRole === "Judge";
        const isContributorRole = userRole === "contributor" || userRole === "Content Creator";
        
        if (isUserRole && contestIdForNavigation) {
          // Check if contest has started - use data from backend response
          const contestHasStarted = data.contest_has_started === true;
          const needsWaitingRoom = data.waiting_room === true;
          
          if (contestHasStarted) {
            // Contest is live - proceed to voting panel
            if (data.already_joined) {
              navigate(`/photo-voting/${contestIdForNavigation}`);
            } else {
              setTimeout(() => {
                navigate(`/photo-voting/${contestIdForNavigation}`);
              }, 1000);
            }
          } else if (needsWaitingRoom) {
            // Contest hasn't started - redirect to waiting room
            toast.success("✅ Entry confirmed! Redirecting to waiting room...");
            setTimeout(() => {
              navigate(`/waiting-room/${contestIdForNavigation}`);
            }, 1000);
          } else {
            // No start time set - proceed to voting (legacy behavior)
            if (data.already_joined) {
              navigate(`/photo-voting/${contestIdForNavigation}`);
            } else {
              setTimeout(() => {
                navigate(`/photo-voting/${contestIdForNavigation}`);
              }, 1000);
            }
          }
        }
        
        // For contributors, just show success toast without navigation
        if (isContributorRole && !data.already_joined) {
          // Toast already shown above at line 302, no additional action needed
        }
      } else {
        const errorData = await response.json();
        
        // Check if already joined
        if (errorData.error && errorData.error.includes("already joined")) {
          toast.warning(`⚠️ Already joined "${selectedContestName}"`);
          setJoinedContestIds(prev => [...prev, selectedContestId]);
        } else if (errorData.error && errorData.error.includes("upload")) {
          toast.error(`📸 ${errorData.error}`);
        } else {
          toast.error(`❌ ${errorData.detail || errorData.error || "Failed to join contest"}`);
        }
        
        // Close modal on error
        setIsJoining(false);
        setShowConfirm(false);
        setShowImageSelection(false);
        setSelectedContestId(null);
        setSelectedContestName("");
        setSelectedContestCategory("");
        setSelectedContestDetails(null);
        setUserImages([]);
        setSelectedImageId(null);
      }
    } catch (error) {
      console.error("Error joining contest:", error);
      toast.error("❌ An error occurred while joining the contest");
      setIsJoining(false);
      setShowConfirm(false);
      setShowImageSelection(false);
      setSelectedContestId(null);
      setSelectedContestName("");
      setSelectedContestCategory("");
      setSelectedContestDetails(null);
      setUserImages([]);
      setSelectedImageId(null);
    }
  };

  const applyFilters = (selectedOptions) => {
    setSelectedParts(selectedOptions);
    const sourceList = filterExpiredContests(allContests);

    if (!selectedOptions.length) {
      setContests(sourceList);
      return;
    }

    const values = selectedOptions.map((o) => o.value);
    const filtered = sourceList.filter((c) => values.includes(c.category));
    setContests(filtered);
  };

  const handleViewResults = (contest) => {
    // Mock detailed results data - in real app this would come from API
    const mockResults = [
      { place: 1, screenName: "alice_w", votes: 657 },
      { place: 2, screenName: "bob_k", votes: 456 },
      { place: 3, screenName: "charlie_z", votes: 334 },
      { place: 4, screenName: "david_m", votes: 232 },
      { place: 5, screenName: "eve_s", votes: 213 },
      { place: 6, screenName: "frank_l", votes: 199 },
      { place: 7, screenName: "grace_h", votes: 189 },
    ];
    
    setSelectedContestResults({
      contestName: contest.contest,
      results: mockResults
    });
    setShowResultsModal(true);
  };

  const closeResultsModal = () => {
    setShowResultsModal(false);
    setSelectedContestResults(null);
  };

  // Fetch contests from API
  useEffect(() => {
    const fetchContests = async () => {
      try {
        setLoading(true);
        
        if (!profile?.accessToken) {
          console.error('No authentication token found');
          setLoading(false);
          return;
        }
        
        const response = await fetch(`${API_BASE}accounts/contests/?t=${Date.now()}`, {
          headers: {
            'Authorization': `Bearer ${profile.accessToken}`,
            'Content-Type': 'application/json'
          },
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const filtered = filterExpiredContests(data);
        setAllContests(data);
        setContests(filtered);
      } catch (err) {
        console.error('Error fetching contests:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContests();
  }, [profile?.accessToken]);

  useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 1024);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Fetch joined contests for contributors
  useEffect(() => {
    const fetchJoinedContests = async () => {
      const token = profile?.accessToken;
      if (!token || userRole !== "contributor") return;

      try {
        const response = await fetch(`${API_BASE}accounts/contests/my-contests/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const joinedIds = data.map(c => c.id);
          setJoinedContestIds(joinedIds);
        }
      } catch (error) {
        console.error("Error fetching joined contests:", error);
      }
    };

    if (profile?.accessToken) {
      fetchJoinedContests();
    }
  }, [profile, userRole]);

  return (
    <div
      className={`flex flex-col h-auto bg-[#1C1C1E] overflow-y-auto rounded-xl ${
        role === "Creator" ? "md:h-[652px]" : "md:h-[85vh]"
      }`}
    >
      <div className="flex items-center justify-between p-4 shadow-sm">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="text-2xl p-2 rounded-md focus:outline-none"
        >
          <FilterIcon />
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-[#ceb46a]">Contests</h1>
        <div className="w-10" />
      </div>

      {/* Contest Cards */}
      <div className="px-4 md:px-6 pb-6">
        {showFilters && (
          <div className="mb-4">
            <Select
              isMulti
              value={selectedParts}
              options={bodyPartOptions}
              onChange={applyFilters}
              placeholder="Filter by body part..."
              className="text-black"
            />
          </div>
        )}

        {loading ? (
          <div className="text-center text-white py-10">Loading contests...</div>
        ) : (
          <div
            className={`grid gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 ${
              showFilters ? "xl:grid-cols-3" : "xl:grid-cols-4"
            }`}
          >
            {contests.map((contest) => {
              // Parse attributes JSON string
              let parsedAttributes = {};
              try {
                parsedAttributes = typeof contest.attributes === 'string' 
                  ? JSON.parse(contest.attributes)
                  : contest.attributes || {};
              } catch (e) {
                console.error('Error parsing attributes for contest:', contest.id, e);
              }

              // Define available options for each category (excluding "All")
              const categoryOptions = {
                "Gender": ["Male", "Female", "Non-binary", "Other"],
                "Age": ["18-21", "22-25", "26-29", "30-34", "35-40", "40+"],
                "Skin Tone": ["Light", "Medium", "Dark"],
                "Body Type": ["Petite", "Short & Curvy", "Avg/Athletic", "Tall & Slim", "Plus Size", "Short & Fit", "Short & Broad", "Tall & Fit", "Big & Tall"],
                "Hair Color": ["Blonde", "Brown", "Black", "Red", "Auburn", "Dirty Blonde", "Strawberry Blonde"],
                "Shoe Size": ["<6", "6-7.5", "8-9.5", "10+", "<8.5", "8.5-10", "10.5-12.5", "13+"],
                "Bust Size": ["A", "B", "C", "D", "AA", "DD/E", "DDD/F", ">DDD"],
                "Penis Size": ["<4", "4-5.5", "6-7.5", "8-9.5", "10+"]
              };

              // Helper function to check if all options are selected and return display value with tooltip info
              const getAttributeDisplay = (category, attr) => {
                if (!attr || (Array.isArray(attr) && attr.length === 0)) {
                  return { display: "N/A", isAll: false, values: [] };
                }
                
                const values = Array.isArray(attr) ? attr : [attr];
                const availableOptions = categoryOptions[category] || [];
                
                // Check if all available options are selected (excluding "All" itself)
                const allSelected = availableOptions.length > 0 && 
                  availableOptions.every(opt => values.includes(opt));
                
                if (allSelected) {
                  return { 
                    display: "All", 
                    isAll: true, 
                    values: availableOptions 
                  };
                }
                
                // If not all selected, show the values
                return { 
                  display: values.join(", "), 
                  isAll: false, 
                  values: values 
                };
              };

              return (
            <div key={contest.id} className="bg-black rounded-lg overflow-hidden">
              <div className="relative h-48 overflow-hidden rounded-t-lg">
                <img
                  src={contest.image || Image1}
                  alt={contest.title || 'Contest'}
                  className="w-full h-60 object-contain rounded-md mb-3"
                  onError={(e) => { e.target.src = Image1; }}
                />
                {/* Recurring Badge */}
                {contest.recurring && contest.recurring !== "none" && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="px-3 py-1 rounded-md text-xs font-bold bg-[#CEB46A] text-black shadow-lg uppercase">
                      {contest.recurring === "daily" ? "Daily" 
                        : contest.recurring === "weekly" ? "Weekly"
                        : contest.recurring === "monthly" ? "Monthly"
                        : contest.recurring}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <h3 className="text-white text-2xl md:text-3xl font-bold">
                    {contest.title || 'Contest'}
                  </h3>
                </div>
              </div>
              <div className="p-4">
                <div className="text-white text-sm mb-2 space-y-1">
                  <p>
                    <span className="font-bold text-white">Body Part:</span>{" "}
                    {contest.category || "N/A"}
                  </p>
                  {(() => {
                    const genderDisplay = getAttributeDisplay("Gender", parsedAttributes.Gender);
                    return (
                      <p>
                        <span className="font-bold text-white">Gender:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {genderDisplay.display}
                          {genderDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{genderDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {(() => {
                    const ageDisplay = getAttributeDisplay("Age", parsedAttributes.Age);
                    return (
                      <div>
                        <span className="font-bold text-white">Age Range:</span>{" "}
                        {ageDisplay.isAll ? (
                          <span className="relative inline-flex items-center group">
                            All
                            <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                            <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 min-w-[72px] z-50 border border-[#ceb46a] shadow-lg">
                              <span className="flex flex-col gap-0.5">
                                {ageDisplay.values.map(v => (
                                  <span key={v} className="whitespace-nowrap">{v}</span>
                                ))}
                              </span>
                            </span>
                          </span>
                        ) : (
                          <span className="inline-flex flex-col gap-0.5 align-top">
                            {ageDisplay.values.map(v => (
                              <span key={v} className="block whitespace-nowrap">{v}</span>
                            ))}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {(() => {
                    const bodyTypeDisplay = getAttributeDisplay("Body Type", parsedAttributes["Body Type"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Body Type:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {bodyTypeDisplay.display}
                          {bodyTypeDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{bodyTypeDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {(() => {
                    const skinToneDisplay = getAttributeDisplay("Skin Tone", parsedAttributes["Skin Tone"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Skin Tone:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {skinToneDisplay.display}
                          {skinToneDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{skinToneDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {(() => {
                    const hairColorDisplay = getAttributeDisplay("Hair Color", parsedAttributes["Hair Color"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Hair Color:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {hairColorDisplay.display}
                          {hairColorDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{hairColorDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {/* 6th Optional Attributes */}
                  {parsedAttributes["Bust Size"] && (() => {
                    const bustSizeDisplay = getAttributeDisplay("Bust Size", parsedAttributes["Bust Size"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Bust Size:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {bustSizeDisplay.display}
                          {bustSizeDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{bustSizeDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {parsedAttributes["Shoe Size"] && (() => {
                    const shoeSizeDisplay = getAttributeDisplay("Shoe Size", parsedAttributes["Shoe Size"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Shoe Size:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {shoeSizeDisplay.display}
                          {shoeSizeDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{shoeSizeDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  {parsedAttributes["Penis Size"] && (() => {
                    const penisSizeDisplay = getAttributeDisplay("Penis Size", parsedAttributes["Penis Size"]);
                    return (
                      <p>
                        <span className="font-bold text-white">Penis Size:</span>{" "}
                        <span className="relative inline-flex items-center group">
                          {penisSizeDisplay.display}
                          {penisSizeDisplay.isAll && (
                            <>
                              <span className="ml-1 text-[#ceb46a] cursor-help text-xs">ⓘ</span>
                              <span className="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-3 py-2 whitespace-normal max-w-xs z-50 border border-[#ceb46a] shadow-lg">
                                <span className="block">{penisSizeDisplay.values.join(", ")}</span>
                              </span>
                            </>
                          )}
                        </span>
                      </p>
                    );
                  })()}
                  <p>
                    <span className="font-bold text-white">
                      Contributors Joined:
                    </span>{" "}
                    {contest.joined || 0}/{contest.max_participants || 0}
                  </p>
                  {/* Show estimated prize only to contributors (75% of user entry fees) */}
                  {userRole === "contributor" && (
                    <p>
                      <span className="font-bold text-[#ceb46a]">
                        Estimated Prize:
                      </span>{" "}
                      <span className="text-[#ceb46a] font-semibold">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(contest.estimated_prize || 0))}
                      </span>
                      {contest.user_entries_count > 0 && (
                        <span className="text-gray-400 text-xs ml-2">
                          ({contest.user_entries_count} user{contest.user_entries_count !== 1 ? 's' : ''} entered)
                        </span>
                      )}
                    </p>
                  )}
                </div>
                {(() => {
                  const now = new Date();
                  const startTime = new Date(contest.start_time);
                  const endTime = new Date(contest.end_time);
                  const hasStarted = now >= startTime;
                  const hasEnded = now >= endTime;

                  if (hasEnded) {
                    return (
                      <>
                        <p className="text-gray-400 font-bold text-sm mb-2">
                          Contest Ended
                        </p>
                        <p className="text-gray-400 font-bold text-sm mb-4">
                          &nbsp;
                        </p>
                      </>
                    );
                  } else if (hasStarted) {
                    return (
                      <>
                        <p className="text-green-400 font-bold text-sm mb-2">
                          In Progress
                        </p>
                        <p className="text-red-400 font-bold text-sm mb-4">
                          <CountdownTimer targetTime={contest.end_time} label="Ends In" expiredText="Contest Ended" />
                        </p>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <p className="text-green-400 font-bold text-sm mb-2">
                          <CountdownTimer targetTime={contest.start_time} label="Starts In" expiredText="Starting..." />
                        </p>
                        <p className="text-red-400 font-bold text-sm mb-4">
                          <CountdownTimer targetTime={contest.end_time} label="Ends In" expiredText="Contest Ended" />
                        </p>
                      </>
                    );
                  }
                })()}
                {(() => {
                  const now = new Date();
                  const startTime = new Date(contest.start_time);
                  const endTime = new Date(contest.end_time);
                  const hasStarted = now >= startTime;
                  const hasEnded = now >= endTime;
                  
                  // Don't show any button if contest has ended
                  if (hasEnded) {
                    return (
                      <button
                        disabled
                        className="w-full flex items-center justify-center gap-2 bg-gray-600 text-gray-400 rounded-md font-semibold text-sm px-3 py-1.5 cursor-not-allowed"
                      >
                        CONTEST ENDED
                      </button>
                    );
                  }
                  
                  // Users can now enter before contest starts (to pay), but voting is restricted
                  const canUserEnter = true; // Always allow users to enter/pay
                  
                  // Show JOIN button for contributors, Enter button for users
                  return (
                    <button
                      onClick={() => canUserEnter && handleEnterClick(contest)}
                      disabled={!canUserEnter}
                      className={`w-full flex items-center justify-center gap-2 rounded-md font-semibold text-sm px-3 py-1.5 transition ${
                        canUserEnter
                          ? "bg-[#ceb46a] text-black hover:opacity-90"
                          : "bg-gray-600 text-gray-400 cursor-not-allowed"
                      }`}
                    >
          {(userRole === "contributor" || userRole === "Content Creator") ? (
            <>JOIN</>
          ) : (
                        (() => {
                          const rawCost = contest?.cost ?? contest?.entry_fee ?? 0;
                          const numericCost = Number(rawCost);
                          const hasCost = Number.isFinite(numericCost) && numericCost > 0;
                          const formattedCost = hasCost
                            ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(numericCost)
                            : "Free";
                          
                          return (
                            <>
                              <span className="px-2 py-1 bg-white text-black text-xs font-bold rounded-full border border-gray-400">
                                {formattedCost}
                              </span>
                              {hasStarted ? (
                                "Enter & Vote"
                              ) : (
                                <span className="flex flex-col items-center leading-tight">
                                  <span>Enter</span>
                                  <span className="text-[10px] leading-tight">(vote when live)</span>
                                </span>
                              )}
                            </>
                          );
                        })()
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>
              );
            })}

            {contests.length === 0 && (
              <p className="text-center text-gray-400 col-span-full mt-10">
                No contests found for selected filters.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Image Selection Modal */}
      <ImageSelectionModal
        isOpen={showImageSelection}
        onClose={() => {
          setShowImageSelection(false);
          setSelectedContestId(null);
          setSelectedContestName("");
          setSelectedContestCategory("");
          setSelectedContestDetails(null);
          setUserImages([]);
        }}
        images={userImages}
        contestName={selectedContestName}
        contestCategory={selectedContestCategory}
        onConfirm={handleImageSelection}
        isJoining={isJoining}
      />

      {/* Photo Capture Prompt Modal */}
      <PhotoCapturePromptModal
        isOpen={showPhotoCapture}
        onClose={() => {
          setShowPhotoCapture(false);
          setSelectedContestId(null);
          setSelectedContestName("");
          setSelectedContestCategory("");
          setSelectedContestDetails(null);
        }}
        contestName={selectedContestName}
        contestCategory={selectedContestCategory}
      />

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-[#1C1C1E] border border-[#ceb46a] rounded-xl p-6 w-full max-w-md shadow-lg">
            <h2 className="text-xl font-bold text-[#ceb46a] mb-4">
              {isVotingOnly 
                ? (() => {
                    const now = new Date();
                    const contestStartTime = new Date(selectedContestDetails?.start_time);
                    const contestHasStarted = now >= contestStartTime;
                    return contestHasStarted ? "Ready to Start Voting?" : "Enter Contest";
                  })()
                : "Confirm Join Contest"}
            </h2>
            <p className="text-white mb-4">
              {isVotingOnly
                ? (() => {
                    const now = new Date();
                    const contestStartTime = new Date(selectedContestDetails?.start_time);
                    const contestHasStarted = now >= contestStartTime;
                    
                    if (contestHasStarted) {
                      return `You're about to enter "${selectedContestName}" as a voter. You'll be able to cast votes immediately.`;
                    } else {
                      return `You're about to enter "${selectedContestName}" as a voter. The contest hasn't started yet, so you'll be able to vote once it goes live.`;
                    }
                  })()
                : "Do you want to join this contest as a contributor?"}
            </p>
            <p className="text-gray-400 text-sm mb-2">
              Contest: <strong className="text-[#ceb46a]">{selectedContestName}</strong>
            </p>
            {isVotingOnly && selectedContestDetails?.category && (
              <p className="text-gray-400 text-sm mb-2">
                Body Part: <strong className="text-white">{selectedContestDetails.category}</strong>
              </p>
            )}
            {isVotingOnly && selectedContestDetails && (
              <p className="text-gray-400 text-sm mb-4">
                Entry Fee:{" "}
                <strong className="text-white">
                  {(() => {
                    const rawCost = selectedContestDetails?.cost ?? selectedContestDetails?.entry_fee ?? 0;
                    const numericCost = Number(rawCost);
                    if (!Number.isFinite(numericCost) || numericCost <= 0) return "Free";
                    return new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                    }).format(numericCost);
                  })()}
                </strong>
              </p>
            )}
            {!isVotingOnly && selectedImageId && userImages.length > 0 && (
              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-2">Selected Image:</p>
                <div className="flex items-center gap-3 bg-gray-800 p-3 rounded-lg">
                  <img
                    src={userImages.find(img => img.id === selectedImageId)?.image_url || userImages.find(img => img.id === selectedImageId)?.image}
                    alt="Selected"
                    className="w-16 h-16 object-cover rounded"
                  />
                  <div className="text-white text-sm">
                    <p className="font-semibold">{selectedContestCategory}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 text-sm border border-gray-500 rounded text-gray-300 hover:bg-gray-700/40 transition"
                onClick={() => {
                  setShowConfirm(false);
                  setIsJoining(false);
                  setIsVotingOnly(false);
                  setSelectedContestId(null);
                  setSelectedContestName("");
                  setSelectedContestCategory("");
                  setSelectedContestDetails(null);
                  setUserImages([]);
                  setSelectedImageId(null);
                }}
                disabled={isJoining}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm bg-[#ceb46a] text-black rounded hover:bg-yellow-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={confirmEntry}
                disabled={isJoining}
              >
                {isVotingOnly 
                  ? (() => {
                      const now = new Date();
                      const contestStartTime = new Date(selectedContestDetails?.start_time);
                      const contestHasStarted = now >= contestStartTime;
                      return contestHasStarted ? "Start Voting" : "Enter Contest";
                    })()
                  : isJoining ? "Joining..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contest Results Section (Judges only) */}
      {userRole === "Judge" && (
        <div className="px-4 md:px-6 pb-10">
          <h2 className="text-xl md:text-2xl font-bold text-[#ceb46a] mb-4">
            Contest Results
          </h2>

          <div className="overflow-x-auto rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="bg-[#ceb46a] text-black">
                  {showAllResults ? (
                    <>
                      <th className="py-3 px-4 text-left">Place</th>
                      <th className="py-3 px-4 text-left">Screen Name</th>
                      <th className="py-3 px-4 text-left">Votes</th>
                    </>
                  ) : (
                    <>
                      <th className="py-3 px-4 text-left">Contest</th>
                      <th className="py-3 px-4 text-left">Contest Winner</th>
                      <th className="py-3 px-4 text-left">Date</th>
                      <th className="py-3 px-4 text-left">Results</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {showAllResults
                  ? allContributors.map((c, idx) => (
                      <tr
                        key={idx}
                        className={
                          idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                        }
                      >
                        <td className="py-3 px-4">{c.place}</td>
                        <td className="py-3 px-4">{c.screenName}</td>
                        <td className="py-3 px-4">{c.votes}</td>
                      </tr>
                    ))
                  : topThree.map((c, idx) => (
                      <tr
                        key={idx}
                        className={
                          idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                        }
                      >
                        <td className="py-3 px-4 font-semibold">{c.contest}</td>
                        <td className="py-3 px-4 flex items-center gap-3">
                          <img
                            src={c.image}
                            alt="Contributor"
                            className="w-10 h-10 rounded-full object-cover border border-[#ceb46a]"
                          />
                          <span>{c.username}</span>
                        </td>
                        <td className="py-3 px-4">{c.winDate || c.date || "—"}</td>
                        <td className="py-3 px-4">
                          <button 
                            onClick={() => handleViewResults(c)}
                            className="px-3 py-1 rounded bg-[#ceb46a] text-black font-semibold hover:bg-[#b99d55]"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>

          {/* View All button */}
          <div className="mt-4 text-right">
            <button
              onClick={() => navigate('/contests')}
              className="bg-[#ceb46a] text-black px-4 py-2 rounded font-semibold hover:bg-[#b99d55]"
            >
              View All
            </button>
          </div>
        </div>
      )}

      {/* Detailed Results Modal */}
      {showResultsModal && selectedContestResults && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-[#1C1C1E] text-white rounded-2xl shadow-lg w-3/4 max-w-3xl p-6">
            <h2 className="text-2xl font-bold text-[#ceb46a] mb-4">
              Full Results – {selectedContestResults.contestName}
            </h2>

            <div className="overflow-y-auto max-h-96 custom-scrollbar">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#ceb46a] text-black">
                    <th className="py-3 px-4 text-left">Place</th>
                    <th className="py-3 px-4 text-left">Screen Name</th>
                    <th className="py-3 px-4 text-left">Votes</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedContestResults.results.map((result, idx) => (
                    <tr
                      key={idx}
                      className={
                        idx % 2 === 0 ? "bg-[#161617]" : "bg-[#1C1C1E]"
                      }
                    >
                      <td className="py-3 px-4">{result.place}</td>
                      <td className="py-3 px-4">@{result.screenName}</td>
                      <td className="py-3 px-4">{result.votes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={closeResultsModal}
                className="px-5 py-2 rounded-lg font-semibold
                           bg-[#ceb46a] text-black
                           hover:bg-[#e3c97a] transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
